/**
 * ScenarioBasisSection -- the severe-but-plausible record (P418).
 *
 * An examiner's first question of a scenario is why the bank believes
 * it is severe but plausible, who owns that judgement, who approved it
 * and when it is next looked at. The record has existed since P418 and
 * nothing could write it: the PATCH dropped every field and no screen
 * offered one. This is the screen.
 *
 * The approval time is stamped by the server when an approver is
 * named, never typed; changing the basis clears its approval, because
 * what was approved is no longer what is recorded.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ScenarioDetail } from "shared/api/types";
import { formatDate } from "shared/time/formatDate";
import {
  type ScenarioBasisPatch,
  usePatchScenarioBasis,
} from "twin/api/scenario_hooks";

interface Props {
  scenarioId: string;
  detail: ScenarioDetail;
  canEdit: boolean;
}

const blank = (v: string): string | null => (v.trim() === "" ? null : v.trim());

export function ScenarioBasisSection({ scenarioId, detail, canEdit }: Props) {
  const { t } = useTranslation();
  const patch = usePatchScenarioBasis(scenarioId);
  const [editing, setEditing] = useState(false);
  const [basis, setBasis] = useState(detail.plausibility_basis ?? "");
  const [owner, setOwner] = useState(detail.basis_owner_email ?? "");
  const [approver, setApprover] = useState(detail.basis_approved_by_email ?? "");
  const [review, setReview] = useState(detail.basis_next_review ?? "");

  const approved = !!detail.basis_approved_by_email;
  const basisEdited = blank(basis) !== (detail.plausibility_basis ?? null);
  const base = "screens.scenariosView.basis";

  function open() {
    setBasis(detail.plausibility_basis ?? "");
    setOwner(detail.basis_owner_email ?? "");
    setApprover(detail.basis_approved_by_email ?? "");
    setReview(detail.basis_next_review ?? "");
    setEditing(true);
  }

  // Round 17 R3 -- the thread's "Record the plausibility basis" linked
  // to the page it was on and opened nothing. Arriving by that link
  // opens the form for a seat that may record it.
  // Read off the window, not the router: the section is also drawn
  // outside one (its own tests, a print view).
  const arrivedToRecord =
    typeof window !== "undefined" && window.location.hash === "#plausibility-basis";
  useEffect(() => {
    if (arrivedToRecord && canEdit && !editing) open();
    // Once per arrival; `open` reads the detail it was rendered with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedToRecord, canEdit]);

  function save() {
    // Send only what changed: a key left out is left alone on the server.
    const body: ScenarioBasisPatch = {};
    if (basisEdited) body.plausibility_basis = blank(basis);
    if (blank(owner) !== (detail.basis_owner_email ?? null)) {
      body.basis_owner_email = blank(owner);
    }
    if (blank(approver) !== (detail.basis_approved_by_email ?? null)) {
      body.basis_approved_by_email = blank(approver);
    }
    if (blank(review) !== (detail.basis_next_review ?? null)) {
      body.basis_next_review = blank(review);
    }
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    patch.mutate(body, { onSuccess: () => setEditing(false) });
  }

  return (
    <section
      id="plausibility-basis"
      data-testid="scenario-basis"
      className="rounded border border-slate-200 bg-white p-3 text-sm"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t(`${base}.heading`)}
        </h3>
        {canEdit && !editing && (
          <button
            type="button"
            data-testid="scenario-basis-edit"
            onClick={open}
            className="text-xs font-medium text-brand-brassDark hover:underline"
          >
            {detail.plausibility_basis ? t(`${base}.edit`) : t(`${base}.record`)}
          </button>
        )}
      </div>

      {!editing && (
        <div data-testid="scenario-basis-view" className="mt-2 space-y-1">
          {detail.plausibility_basis ? (
            <p dir="auto" className="whitespace-pre-line text-slate-800">
              {detail.plausibility_basis}
            </p>
          ) : (
            <p className="text-slate-500">{t(`${base}.none`)}</p>
          )}
          <p className="text-xs text-slate-600">
            {t(`${base}.owner`)}: {detail.basis_owner_email ?? t(`${base}.notNamed`)}
          </p>
          <p className="text-xs text-slate-600" data-testid="scenario-basis-approval">
            {approved
              ? t(`${base}.approvedBy`, {
                  name: detail.basis_approved_by_email,
                  date: detail.basis_approved_at
                    ? formatDate(detail.basis_approved_at)
                    : "",
                })
              : t(`${base}.notApproved`)}
          </p>
          <p className="text-xs text-slate-600">
            {t(`${base}.nextReview`)}:{" "}
            {detail.basis_next_review
              ? formatDate(detail.basis_next_review)
              : t(`${base}.notSet`)}
          </p>
        </div>
      )}

      {editing && (
        <form
          data-testid="scenario-basis-form"
          className="mt-2 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <label className="block">
            <span className="text-xs text-slate-600">{t(`${base}.basisLabel`)}</span>
            <textarea
              data-testid="scenario-basis-text"
              dir="auto"
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
            />
          </label>
          {approved && basisEdited && (
            <p data-testid="scenario-basis-clears" className="text-xs text-amber-800">
              {t(`${base}.clearsApproval`)}
            </p>
          )}
          <label className="block">
            <span className="text-xs text-slate-600">{t(`${base}.owner`)}</span>
            <input
              type="email"
              data-testid="scenario-basis-owner"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-600">{t(`${base}.approverLabel`)}</span>
            <input
              type="email"
              data-testid="scenario-basis-approver"
              value={approver}
              onChange={(e) => setApprover(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
            />
            <span className="mt-0.5 block text-[11px] text-slate-500">
              {t(`${base}.approverHint`)}
            </span>
          </label>
          <label className="block">
            <span className="text-xs text-slate-600">{t(`${base}.nextReview`)}</span>
            <input
              type="date"
              data-testid="scenario-basis-review"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1"
            />
          </label>
          {patch.isError && (
            <p data-testid="scenario-basis-error" className="text-xs text-rose-700">
              {t(`${base}.error`)}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              data-testid="scenario-basis-save"
              disabled={patch.isPending}
              className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
            >
              {t(`${base}.save`)}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs text-slate-600 hover:underline"
            >
              {t(`${base}.cancel`)}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
